export interface QuoteBodyTokens {
	firstRecipientFirstName: string | null;
	projectName: string;
	companyName: string;
	userName: string;
	greetingFallback: string;
}

/**
 * The template is HTML and every token value is user data — a contact, project or company name can
 * carry markup, which must render as text, never as elements. Quotes and backticks are covered too
 * so an escaped value stays inert inside an attribute.
 */
const escapeHtml = (value: string): string =>
	value
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;')
		.replace(/`/g, '&#96;');

/** Replacement via a function, so $-sequences in a name insert literally instead of as patterns. */
const insert = (template: string, token: RegExp, value: string): string => template.replace(token, () => escapeHtml(value));

/**
 * Resolves the body's merge tokens ONCE, when the modal opens.
 *
 * The body is editable and the greeting is the user's to adjust, so the tokens have to become real
 * text they can see and change. The mail carries inline HTML with no template behind it, so this is
 * the only place tokens are ever resolved — there is no second site that could overwrite an edited
 * greeting at render time.
 *
 * Deliberately does NOT re-resolve when the recipient list changes: adjusting the greeting after
 * adding someone is the user's job, not something to silently rewrite under their cursor.
 *
 * The markers are [[double_bracket]], NOT {braces}: these templates ship as react-intl messages, and
 * ICU rejects an argument name containing spaces, so a `{contact first name}` template threw on
 * every parse. react-intl swallowed the throw and handed back the raw string — which is why the
 * substitution below still worked — while root.js reported each one to Sentry. Square brackets are
 * literal text to ICU, so the message parses and reaches this function untouched.
 */
export const buildQuoteBody = (template: string, tokens: QuoteBodyTokens): string => {
	let body = insert(template, /\[\[contact_first_name\]\]/g, tokens.firstRecipientFirstName || tokens.greetingFallback);
	body = insert(body, /\[\[project_name\]\]/g, tokens.projectName);
	body = insert(body, /\[\[company_name\]\]/g, tokens.companyName);
	return insert(body, /\[\[user_name\]\]/g, tokens.userName);
};

/**
 * The indicator renders in parentheses; when the project has none, the whole " ([[project_id]])"
 * group is dropped rather than leaving an empty "()" behind. The subject is plain text, so values
 * insert unescaped — only $-expansion needs guarding.
 */
export const buildQuoteSubject = (template: string, projectName: string, projectIndicator?: string | null): string =>
	(projectIndicator
		? template.replace(/\[\[project_id\]\]/g, () => projectIndicator)
		: template.replace(/\s*\(\[\[project_id\]\]\)/g, '')
	).replace(/\[\[project_name\]\]/g, () => projectName);
